<template>
  <div class="page-container">
    <!-- 头部 -->
    <div class="page-header">
      <div>
        <h2 class="page-title">小区管理</h2>
      </div>
      <div class="page-header-extra">
        <span>共 <b>{{ total }}</b> 个小区</span>
        <span class="divider">|</span>
        <span>展示中 <b>{{ statusCount(1) }}</b></span>
        <span class="divider">|</span>
        <span>已下架 <b>{{ statusCount(0) }}</b></span>
      </div>
    </div>

    <!-- 搜索 + 操作 -->
    <el-card class="search-card" shadow="never">
      <div class="search-bar">
        <el-form :inline="true" :model="query" size="small" class="search-form">
          <el-form-item label="关键字">
            <el-input v-model="query.keyword" placeholder="小区名称/地址关键词" clearable style="width: 180px" />
          </el-form-item>
          <el-form-item label="所属基地">
            <el-select v-model="query.base_id" placeholder="全部" clearable style="width: 180px">
              <el-option v-for="o in baseOptions" :key="o.id" :label="o.name" :value="o.id" />
            </el-select>
          </el-form-item>
          <el-form-item label="状态">
            <el-select v-model="query.status" placeholder="全部" clearable style="width: 120px">
              <el-option v-for="s in STAY_COMMUNITY_STATUS" :key="s.value" :label="s.label" :value="s.value" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
            <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
          </el-form-item>
        </el-form>
        <div class="action-bar">
          <el-button type="primary" icon="el-icon-plus" size="small" @click="handleAdd">新增小区</el-button>
        </div>
      </div>
    </el-card>

    <!-- 表格 -->
    <el-card class="table-card" shadow="never">
      <el-table v-loading="loading" :data="list" border size="small" height="100%">
        <!-- 排序 -->
        <el-table-column label="排序" width="70" align="center">
          <template slot-scope="scope">
            <el-input-number
              v-model="scope.row.sort"
              :min="0" :max="9999"
              size="mini"
              controls-position="right"
              class="sort-input"
              @change="val => handleSortChange(scope.row, val)"
            />
          </template>
        </el-table-column>
        <!-- 封面 -->
        <el-table-column label="封面" width="100" align="center">
          <template slot-scope="scope">
            <div v-if="scope.row.cover" class="cover-thumb">
              <el-image :src="scope.row.cover" :preview-src-list="[scope.row.cover]" fit="cover" style="width: 100%; height: 100%" />
            </div>
            <div v-else class="cover-thumb cover-thumb--empty">无图</div>
          </template>
        </el-table-column>
        <!-- 小区名称 -->
        <el-table-column prop="name" label="小区名称" min-width="130" show-overflow-tooltip />
        <!-- 所属基地 -->
        <el-table-column label="所属基地" min-width="130" show-overflow-tooltip>
          <template slot-scope="scope">{{ baseLabel(scope.row.base_id) }}</template>
        </el-table-column>
        <!-- 地址 -->
        <el-table-column prop="address" label="地址" min-width="180" show-overflow-tooltip>
          <template slot-scope="scope">
            <span v-if="scope.row.address">{{ scope.row.address }}</span>
            <span v-else class="cell-muted">—</span>
          </template>
        </el-table-column>
        <!-- 房型 -->
        <el-table-column label="房型" width="80" align="center">
          <template slot-scope="scope">
            <el-tag size="mini" effect="plain">{{ scope.row.room_count || 0 }} 个</el-tag>
          </template>
        </el-table-column>
        <!-- 配套设施 -->
        <el-table-column label="配套设施" width="90" align="center">
          <template slot-scope="scope">
            <el-tag size="mini" effect="plain">{{ scope.row.facility_count || 0 }} 项</el-tag>
          </template>
        </el-table-column>
        <!-- 状态 -->
        <el-table-column label="状态" width="80" align="center">
          <template slot-scope="scope">
            <el-tag :type="communityStatusTagType(scope.row.status)" size="mini" effect="plain">
              {{ communityStatusText(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <!-- 更新时间 -->
        <el-table-column prop="updated_at" label="更新时间" min-width="160" show-overflow-tooltip />
        <!-- 操作 -->
        <el-table-column label="操作" width="200" align="center">
          <template slot-scope="scope">
            <el-button type="text" size="mini" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button
              v-if="scope.row.status !== 1"
              type="text" size="mini"
              @click="handleStatus(scope.row, 1)"
            >展示</el-button>
            <el-button
              v-else
              type="text" size="mini" class="text-warning-btn"
              @click="handleStatus(scope.row, 0)"
            >下架</el-button>
            <el-dropdown class="more-dropdown" trigger="click" @command="cmd => handleMoreCommand(cmd, scope.row)">
              <el-button type="text" size="mini">
                更多操作<i class="el-icon-arrow-down el-icon--right" />
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="facility"><i class="el-icon-goods" /> 配套设施</el-dropdown-item>
                <el-dropdown-item command="room"><i class="el-icon-key" /> 房型</el-dropdown-item>
                <el-dropdown-item command="delete" divided class="danger-item"><i class="el-icon-delete" /> 删除</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-wrap">
        <el-pagination
          :current-page="page"
          :page-size="pageSize"
          :total="total"
          layout="total, prev, pager, next"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>

    <!-- 编辑/新增小区弹框 -->
    <el-dialog
      :title="isEdit ? '编辑小区' : '新增小区'"
      :visible.sync="formVisible"
      width="820px"
      top="3vh"
      class="stay-community-dialog"
      :close-on-click-modal="false"
      append-to-body
    >
      <el-form ref="form" :model="form" :rules="rules" label-width="100px" label-position="top">
        <div class="form-section">
          <div class="form-section-title">基础</div>
          <el-row :gutter="24">
            <el-col :span="12">
              <el-form-item label="所属基地" prop="base_id">
                <el-select v-model="form.base_id" style="width: 100%">
                  <el-option v-for="o in baseOptions" :key="o.id" :label="o.name" :value="o.id" />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="小区名称" prop="name">
                <el-input v-model="form.name" placeholder="如：象山颐园" maxlength="128" show-word-limit />
              </el-form-item>
            </el-col>
          </el-row>
          <el-form-item label="封面图" prop="cover">
            <image-uploader v-model="form.cover" />
          </el-form-item>
          <el-form-item label="小区地址" prop="address">
            <el-input v-model="form.address" maxlength="255" />
          </el-form-item>
        </div>

        <div class="form-section">
          <div class="form-section-title">小区介绍</div>
          <el-form-item label="介绍正文" prop="intro">
            <wechat-editor v-model="form.intro" />
          </el-form-item>
        </div>

        <div class="form-section">
          <div class="form-section-title">状态</div>
          <el-row :gutter="24">
            <el-col :span="12">
              <el-form-item label="排序值" prop="sort">
                <el-input-number v-model="form.sort" :min="0" :max="9999" controls-position="right" style="width: 100%" />
                <span class="form-tip">数字越小，基地详情内越靠前</span>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="是否展示" prop="status">
                <el-radio-group v-model="form.status">
                  <el-radio :label="1">展示</el-radio>
                  <el-radio :label="0">下架</el-radio>
                </el-radio-group>
              </el-form-item>
            </el-col>
          </el-row>
        </div>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="formVisible = false">取 消</el-button>
        <el-button type="primary" :loading="submitting" @click="submit">保 存</el-button>
      </div>
    </el-dialog>

    <!-- 房型管理弹框 -->
    <el-dialog
      :title="roomCommunity ? `房型管理 · ${roomCommunity.name}` : '房型管理'"
      :visible.sync="roomManageVisible"
      width="860px"
      top="5vh"
      class="room-manage-dialog"
      :close-on-click-modal="false"
      append-to-body
    >
      <div class="room-manage-bar">
        <span class="room-manage-tip">展示于小区详情的「房型介绍」板块，按排序值先后展示</span>
        <el-button type="primary" icon="el-icon-plus" size="small" @click="handleRoomAdd">新增房型</el-button>
      </div>

      <div class="room-cards" v-loading="roomLoading">
        <div
          v-for="r in communityRooms"
          :key="r.id"
          class="room-card"
          :class="{ 'is-hidden': r.status !== 1 }"
        >
          <div class="room-card-cover">
            <el-image v-if="r.image" :src="r.image" :preview-src-list="[r.image]" fit="cover" style="width: 100%; height: 100%" />
            <div v-else class="cover-placeholder">
              <i class="el-icon-picture-outline" />
              <span>暂无户型图</span>
            </div>
            <span v-if="r.status !== 1" class="cover-mask">已隐藏</span>
          </div>

          <div class="room-card-main">
            <div class="room-card-head">
              <span class="room-card-name">{{ r.name }}</span>
              <el-tag :type="roomStatusTagType(r.status)" size="mini" effect="plain">
                {{ roomStatusText(r.status) }}
              </el-tag>
            </div>
            <p class="room-card-desc">{{ r.description || '暂无一句话描述' }}</p>
            <div class="room-card-meta">
              <span v-if="r.suitable_guests"><i class="el-icon-user" /> 适住 {{ r.suitable_guests }}</span>
              <span v-if="r.area"><i class="el-icon-full-screen" /> {{ r.area }}</span>
            </div>
            <div v-if="r.facilities && r.facilities.length" class="room-card-tags">
              <span v-for="f in r.facilities" :key="f" class="facility-tag">{{ f }}</span>
            </div>
          </div>

          <div class="room-card-ops">
            <el-button type="text" size="mini" @click="handleRoomEdit(r)">编辑</el-button>
            <el-button type="text" size="mini" class="text-danger-btn" @click="handleRoomDelete(r)">删除</el-button>
          </div>
        </div>

        <div v-if="!communityRooms.length" class="room-empty">
          <i class="el-icon-house" />
          <p>该小区还没有房型，点右上角「新增房型」创建</p>
        </div>
      </div>
    </el-dialog>

    <!-- 新增/编辑房型弹框 -->
    <el-dialog
      :title="isRoomEdit ? '编辑房型' : '新增房型'"
      :visible.sync="roomFormVisible"
      width="720px"
      top="4vh"
      class="room-form-dialog"
      :close-on-click-modal="false"
      append-to-body
    >
      <el-form ref="roomForm" :model="roomForm" :rules="roomRules" label-width="100px" label-position="top">
        <el-row :gutter="24">
          <el-col :span="12">
            <el-form-item label="所属小区">
              <el-input :value="roomCommunity ? roomCommunity.name : ''" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="户型名称" prop="name">
              <el-input v-model="roomForm.name" placeholder="如：两房两厅一卫" maxlength="64" show-word-limit />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="户型图 / 房间图" prop="image">
          <image-uploader v-model="roomForm.image" tip="上传户型图" hint="建议横版图，展示于房型卡片左侧" />
        </el-form-item>
        <el-row :gutter="24">
          <el-col :span="8">
            <el-form-item label="适住人数" prop="suitable_guests">
              <el-input v-model="roomForm.suitable_guests" placeholder="如：2-3" maxlength="15">
                <template slot="append">人</template>
              </el-input>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="面积" prop="area">
              <el-input v-model="roomForm.area" placeholder="如：约76" maxlength="15">
                <template slot="append">㎡</template>
              </el-input>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="排序值" prop="sort">
              <el-input-number v-model="roomForm.sort" :min="0" :max="9999" controls-position="right" style="width: 100%" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="一句话描述" prop="description">
          <el-input
            v-model="roomForm.description"
            type="textarea" :rows="2"
            maxlength="255" show-word-limit
            placeholder="如：雅致见山，清风入室，适合家庭或结伴旅客"
          />
        </el-form-item>
        <el-form-item label="房内设施（最多 8 个）" prop="facilities">
          <el-select
            v-model="roomForm.facilities"
            multiple filterable allow-create default-first-option
            :multiple-limit="8"
            placeholder="选择或输入后回车，如：大床、免费WIFI、空调"
            style="width: 100%"
          >
            <el-option v-for="f in ROOM_FACILITY_PRESETS" :key="f" :label="f" :value="f" />
          </el-select>
        </el-form-item>
        <el-form-item label="是否展示" prop="status">
          <el-radio-group v-model="roomForm.status">
            <el-radio :label="1">展示</el-radio>
            <el-radio :label="0">下架</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="roomFormVisible = false">取 消</el-button>
        <el-button type="primary" :loading="roomSubmitting" @click="submitRoom">保 存</el-button>
      </div>
    </el-dialog>

    <!-- 配套设施管理弹框 -->
    <el-dialog
      :title="facilityCommunity ? `配套设施 · ${facilityCommunity.name}` : '配套设施'"
      :visible.sync="facilityManageVisible"
      width="860px"
      top="5vh"
      class="facility-manage-dialog"
      :close-on-click-modal="false"
      append-to-body
    >
      <div class="form-section facility-intro-section">
        <div class="form-section-title">配套导语</div>
        <wechat-editor v-if="facilityManageVisible" v-model="facilityIntroEdit" />
        <div class="facility-intro-actions">
          <span class="form-tip">「配套介绍」板块顶部的导语文案</span>
          <el-button type="primary" size="mini" @click="saveFacilityIntro">保存导语</el-button>
        </div>
      </div>

      <div class="form-section facility-list-section">
        <div class="facility-bar">
          <span class="form-section-title facility-bar-title">设施明细（{{ communityFacilities.length }}）</span>
          <el-button type="primary" icon="el-icon-plus" size="small" @click="handleFacilityAdd">新增配套</el-button>
        </div>

        <div class="facility-cards" v-loading="facilityLoading">
          <div
            v-for="f in communityFacilities"
            :key="f.id"
            class="facility-row"
            :class="{ 'is-hidden': f.status !== 1 }"
          >
            <div class="facility-row-imgs">
              <template v-if="f.images && f.images.length">
                <div v-for="(img, gi) in f.images.slice(0, 3)" :key="gi" class="facility-img">
                  <el-image :src="img" :preview-src-list="f.images" :initial-index="gi" fit="cover" style="width: 100%; height: 100%" />
                </div>
              </template>
              <div v-else class="facility-img facility-img--empty">
                <i class="el-icon-picture-outline" />
                <span>暂无图</span>
              </div>
            </div>

            <div class="facility-row-main">
              <div class="facility-row-head">
                <span class="facility-row-name">{{ f.name || '未命名设施' }}</span>
                <el-tag size="mini" effect="plain" class="facility-cat-tag">{{ f.category || '未分类' }}</el-tag>
                <el-tag v-if="f.status !== 1" size="mini" type="info" effect="plain">已隐藏</el-tag>
              </div>
              <p class="facility-row-intro">{{ f.intro || '暂无一句话简介' }}</p>
              <span class="facility-row-sort">排序 {{ f.sort }}</span>
            </div>

            <div class="facility-row-ops">
              <el-button type="text" size="mini" @click="handleFacilityEdit(f)">编辑</el-button>
              <el-button type="text" size="mini" class="text-danger-btn" @click="handleFacilityDelete(f)">删除</el-button>
            </div>
          </div>

          <div v-if="!communityFacilities.length" class="room-empty">
            <i class="el-icon-goods" />
            <p>还没有配套设施，点右上角「新增配套」创建</p>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- 新增/编辑配套弹框 -->
    <el-dialog
      :title="isFacilityEdit ? '编辑配套' : '新增配套'"
      :visible.sync="facilityFormVisible"
      width="640px"
      top="6vh"
      class="facility-form-dialog"
      :close-on-click-modal="false"
      append-to-body
    >
      <el-form ref="facilityForm" :model="facilityForm" :rules="facilityRules" label-width="100px" label-position="top">
        <el-row :gutter="24">
          <el-col :span="12">
            <el-form-item label="类别" prop="category">
              <el-select v-model="facilityForm.category" placeholder="选择或输入" allow-create filterable style="width: 100%">
                <el-option v-for="c in FACILITY_CATEGORY_OPTIONS" :key="c" :label="c" :value="c" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="设施名称" prop="name">
              <el-input v-model="facilityForm.name" placeholder="如：社区食堂" maxlength="32" show-word-limit />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="一句话简介" prop="intro">
          <el-input
            v-model="facilityForm.intro"
            type="textarea" :rows="2"
            maxlength="64" show-word-limit
            placeholder="如：一日三餐，桂林本地口味"
          />
        </el-form-item>
        <el-form-item label="设施图集（最多 3 张）" prop="images">
          <multi-image-uploader v-model="facilityForm.images" dir="stay/facility" :max="3" />
        </el-form-item>
        <el-row :gutter="24">
          <el-col :span="12">
            <el-form-item label="排序值" prop="sort">
              <el-input-number v-model="facilityForm.sort" :min="0" :max="9999" controls-position="right" style="width: 100%" />
              <span class="form-tip">数字越小，小程序端越靠前</span>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="是否展示" prop="status">
              <el-radio-group v-model="facilityForm.status">
                <el-radio :label="1">展示</el-radio>
                <el-radio :label="0">隐藏</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="facilityFormVisible = false">取 消</el-button>
        <el-button type="primary" :loading="facilitySubmitting" @click="submitFacility">保 存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  STAY_COMMUNITY_STATUS, FACILITY_CATEGORY_OPTIONS,
  communityStatusText, communityStatusTagType
} from '@/mock/stayCommunity'
import { ROOM_FACILITY_PRESETS, roomStatusText, roomStatusTagType } from '@/mock/stayRoom'
import {
  getStayBaseOptions,
  getStayCommunityList, createStayCommunity, updateStayCommunity,
  deleteStayCommunity, setStayCommunityStatus, setStayCommunitySort,
  saveStayCommunityFacilityIntro,
  getStayRoomList, createStayRoom, updateStayRoom, deleteStayRoom,
  getStayFacilityList, createStayFacility, updateStayFacility, deleteStayFacility
} from '@/api/stay'
import ImageUploader from '@/components/ImageUploader.vue'
import MultiImageUploader from '@/components/MultiImageUploader.vue'
import WechatEditor from '@/components/WechatEditor.vue'

export default {
  name: 'StayCommunityManage',
  components: { ImageUploader, MultiImageUploader, WechatEditor },
  data() {
    return {
      loading: false,
      list: [],
      total: 0,
      page: 1,
      pageSize: 10,
      // 头部统计（后端全量计数，不受当页筛选影响）
      statusCounts: {},
      // 基地下拉（/stay/base/options）
      baseOptions: [],
      query: { keyword: '', base_id: '', status: '' },
      formVisible: false,
      currentRow: null,
      submitting: false,
      form: this.emptyForm(),
      rules: {
        base_id: [{ required: true, message: '请选择所属基地', trigger: 'change' }],
        name: [{ required: true, message: '请输入小区名称', trigger: 'blur' }],
        status: [{ required: true, message: '请选择状态', trigger: 'change' }]
      },
      // 房型管理
      roomManageVisible: false,
      roomCommunity: null,
      roomLoading: false,
      roomList: [],
      roomFormVisible: false,
      currentRoom: null,
      roomSubmitting: false,
      roomForm: this.emptyRoomForm(),
      roomRules: {
        name: [{ required: true, message: '请输入户型名称', trigger: 'blur' }],
        status: [{ required: true, message: '请选择状态', trigger: 'change' }]
      },
      // 配套设施管理
      facilityManageVisible: false,
      facilityCommunity: null,
      facilityLoading: false,
      facilityList: [],
      facilityIntroEdit: '',
      facilityFormVisible: false,
      currentFacility: null,
      facilitySubmitting: false,
      facilityForm: this.emptyFacilityForm(),
      facilityRules: {
        name: [{ required: true, message: '请输入设施名称', trigger: 'blur' }]
      },
      STAY_COMMUNITY_STATUS,
      FACILITY_CATEGORY_OPTIONS,
      ROOM_FACILITY_PRESETS
    }
  },
  computed: {
    isEdit() { return !!(this.currentRow && this.currentRow.id) },
    isRoomEdit() { return !!(this.currentRoom && this.currentRoom.id) },
    isFacilityEdit() { return !!(this.currentFacility && this.currentFacility.id) },
    // 当前小区的房型（接口已按 sort 排序，前端再兜一层）
    communityRooms() {
      if (!this.roomCommunity) return []
      return this.roomList
        .filter(r => r.community_id === this.roomCommunity.id)
        .sort((a, b) => a.sort - b.sort)
    },
    // 当前小区的配套设施
    communityFacilities() {
      if (!this.facilityCommunity) return []
      return this.facilityList
        .filter(f => f.scope_id === this.facilityCommunity.id)
        .sort((a, b) => a.sort - b.sort)
    }
  },
  created() {
    this.loadBaseOptions()
    this.loadData()
  },
  methods: {
    // ===== 主列表：服务端分页 + 筛选（keyword 模糊 / base_id、status 精确，排序后端保证） =====
    loadData() {
      this.loading = true
      const params = { page: this.page, page_size: this.pageSize }
      const keyword = (this.query.keyword || '').trim()
      if (keyword) params.keyword = keyword
      if (this.query.base_id !== '' && this.query.base_id !== null) params.base_id = this.query.base_id
      if (this.query.status !== '' && this.query.status !== null) params.status = this.query.status

      getStayCommunityList(params).then(res => {
        if (res.code !== 200) return
        const data = res.data || {}
        this.list = data.list || []
        this.total = data.total || 0
        this.statusCounts = data.status_counts || {}
      }).finally(() => {
        this.loading = false
      })
    },
    loadBaseOptions() {
      getStayBaseOptions().then(res => {
        if (res.code === 200) this.baseOptions = res.data || []
      }).catch(() => {})
    },
    handlePageChange(val) {
      this.page = val
      this.loadData()
    },
    statusCount(val) { return this.statusCounts[val] || 0 },
    communityStatusText,
    communityStatusTagType,
    baseLabel(baseId) {
      const item = this.baseOptions.find(o => o.id === baseId)
      return item ? item.name : '—'
    },
    roomStatusText,
    roomStatusTagType,
    handleSearch() {
      this.page = 1
      this.loadData()
    },
    resetSearch() {
      this.query = { keyword: '', base_id: '', status: '' }
      this.page = 1
      this.loadData()
    },
    handleAdd() {
      this.currentRow = null
      this.form = this.emptyForm()
      this.formVisible = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
    },
    handleEdit(row) {
      this.currentRow = Object.assign({}, row)
      this.form = Object.assign(this.emptyForm(), row)
      this.formVisible = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
    },
    handleMoreCommand(cmd, row) {
      if (cmd === 'room') this.handleOpenRooms(row)
      else if (cmd === 'facility') this.handleOpenFacility(row)
      else if (cmd === 'delete') this.handleDelete(row)
    },
    /* ===== 配套设施管理 ===== */
    handleOpenFacility(row) {
      this.facilityCommunity = Object.assign({}, row)
      this.facilityIntroEdit = row.facility_intro || ''
      this.facilityList = []
      this.facilityManageVisible = true
      this.loadFacilities()
    },
    loadFacilities() {
      if (!this.facilityCommunity) return
      this.facilityLoading = true
      getStayFacilityList({ scope: 2, scope_id: this.facilityCommunity.id }).then(res => {
        if (res.code === 200) this.facilityList = (res.data || {}).list || []
      }).finally(() => {
        this.facilityLoading = false
      })
    },
    saveFacilityIntro() {
      saveStayCommunityFacilityIntro(this.facilityCommunity.id, { facility_intro: this.facilityIntroEdit }).then(res => {
        if (res.code === 200) {
          // 同步弹框标题与主列表行（updated_at 以后端为准，刷新列表）
          this.facilityCommunity = Object.assign({}, this.facilityCommunity, { facility_intro: this.facilityIntroEdit })
          this.loadData()
          this.$message.success('导语已保存')
        }
      })
    },
    handleFacilityAdd() {
      if (!this.facilityCommunity) return
      this.currentFacility = null
      this.facilityForm = this.emptyFacilityForm()
      this.facilityForm.scope_id = this.facilityCommunity.id
      this.facilityForm.sort = (this.communityFacilities.length + 1) * 10
      this.facilityFormVisible = true
      this.$nextTick(() => this.$refs.facilityForm && this.$refs.facilityForm.clearValidate())
    },
    handleFacilityEdit(row) {
      this.currentFacility = Object.assign({}, row)
      this.facilityForm = Object.assign(this.emptyFacilityForm(), row, {
        images: Array.isArray(row.images) ? row.images.slice() : []
      })
      this.facilityFormVisible = true
      this.$nextTick(() => this.$refs.facilityForm && this.$refs.facilityForm.clearValidate())
    },
    handleFacilityDelete(row) {
      this.$confirm(`确认删除配套「${row.name || '未命名'}」？`, '删除确认', { type: 'warning' })
        .then(() => {
          deleteStayFacility(row.id).then(res => {
            if (res.code === 200) {
              this.$message.success('已删除')
              this.loadFacilities()
              this.loadData()
            }
          })
        }).catch(() => {})
    },
    submitFacility() {
      this.$refs.facilityForm.validate(valid => {
        if (!valid) return
        const payload = Object.assign({}, this.facilityForm)
        payload.images = Array.isArray(payload.images) ? payload.images.slice() : []
        this.facilitySubmitting = true
        const request = payload.id ? updateStayFacility(payload.id, payload) : createStayFacility(payload)
        request.then(res => {
          if (res.code !== 200) return
          this.$message.success(payload.id ? '已保存' : '已新增配套')
          this.facilityFormVisible = false
          this.loadFacilities()
          this.loadData()
        }).finally(() => {
          this.facilitySubmitting = false
        })
      })
    },
    emptyFacilityForm() {
      return {
        id: 0,
        scope_id: null,
        category: '',
        name: '',
        intro: '',
        images: [],
        sort: 0,
        status: 1
      }
    },
    handleSortChange(row, val) {
      if (val === undefined || val === null) return
      setStayCommunitySort(row.id, { sort: val }).then(res => {
        if (res.code === 200) {
          this.$message.success('排序已更新')
          this.loadData()
        }
      })
    },
    handleStatus(row, status) {
      const action = status === 1 ? '展示' : '下架'
      this.$confirm(`确认${action}「${row.name}」？${status === 1 ? '展示后小程序端可见。' : '下架后小程序端不再展示。'}`, action + '确认', { type: 'warning' })
        .then(() => {
          setStayCommunityStatus(row.id, { status }).then(res => {
            if (res.code === 200) {
              this.$message.success('已' + action)
              this.loadData()
            }
          })
        }).catch(() => {})
    },
    handleDelete(row) {
      this.$confirm(`确认删除「${row.name || '该小区'}」？小区下的房型与配套设施需先行处理。`, '删除确认', { type: 'warning' })
        .then(() => {
          deleteStayCommunity(row.id).then(res => {
            if (res.code === 200) {
              this.$message.success('已删除')
              this.loadData()
            }
          })
        }).catch(() => {})
    },
    submit() {
      this.$refs.form.validate(valid => {
        if (!valid) return
        const payload = Object.assign({}, this.form)
        this.submitting = true
        const request = payload.id ? updateStayCommunity(payload.id, payload) : createStayCommunity(payload)
        request.then(res => {
          if (res.code !== 200) return
          this.$message.success(payload.id ? '已保存' : '已新增')
          this.formVisible = false
          this.loadData()
        }).finally(() => {
          this.submitting = false
        })
      })
    },
    emptyForm() {
      return {
        id: 0,
        base_id: null,
        name: '',
        cover: '',
        address: '',
        intro: '',
        facility_intro: '',
        sort: 0,
        status: 1
      }
    },
    /* ===== 房型管理 ===== */
    handleOpenRooms(row) {
      this.roomCommunity = Object.assign({}, row)
      this.roomList = []
      this.roomManageVisible = true
      this.loadRooms()
    },
    loadRooms() {
      if (!this.roomCommunity) return
      this.roomLoading = true
      getStayRoomList(this.roomCommunity.id).then(res => {
        if (res.code === 200) this.roomList = (res.data || {}).list || []
      }).finally(() => {
        this.roomLoading = false
      })
    },
    handleRoomAdd() {
      if (!this.roomCommunity) return
      this.currentRoom = null
      this.roomForm = this.emptyRoomForm()
      this.roomForm.community_id = this.roomCommunity.id
      this.roomForm.sort = (this.communityRooms.length + 1) * 10
      this.roomFormVisible = true
      this.$nextTick(() => this.$refs.roomForm && this.$refs.roomForm.clearValidate())
    },
    handleRoomEdit(row) {
      this.currentRoom = Object.assign({}, row)
      this.roomForm = Object.assign(this.emptyRoomForm(), row, {
        facilities: Array.isArray(row.facilities) ? row.facilities.slice() : [],
        // 单位由输入框 append 槽展示，编辑回填时剥掉，避免重复
        suitable_guests: this.stripUnit(row.suitable_guests, '人'),
        area: this.stripUnit(row.area, '㎡')
      })
      this.roomFormVisible = true
      this.$nextTick(() => this.$refs.roomForm && this.$refs.roomForm.clearValidate())
    },
    handleRoomDelete(row) {
      this.$confirm(`确认删除房型「${row.name || '未命名'}」？`, '删除确认', { type: 'warning' })
        .then(() => {
          deleteStayRoom(row.id).then(res => {
            if (res.code === 200) {
              this.$message.success('已删除')
              this.loadRooms()
              this.loadData()
            }
          })
        }).catch(() => {})
    },
    submitRoom() {
      this.$refs.roomForm.validate(valid => {
        if (!valid) return
        const payload = Object.assign({}, this.roomForm)
        payload.facilities = Array.isArray(payload.facilities) ? payload.facilities.slice() : []
        // 单位统一由前端补齐（输入框只展示固定 append 槽），工作人员不用手写
        payload.suitable_guests = this.withUnit(payload.suitable_guests, '人')
        payload.area = this.withUnit(payload.area, '㎡')
        this.roomSubmitting = true
        const request = payload.id ? updateStayRoom(payload.id, payload) : createStayRoom(payload)
        request.then(res => {
          if (res.code !== 200) return
          this.$message.success(payload.id ? '已保存' : '已新增房型')
          this.roomFormVisible = false
          this.loadRooms()
          this.loadData()
        }).finally(() => {
          this.roomSubmitting = false
        })
      })
    },
    emptyRoomForm() {
      return {
        id: 0,
        community_id: null,
        name: '',
        image: '',
        suitable_guests: '',
        area: '',
        description: '',
        facilities: [],
        sort: 0,
        status: 1
      }
    },
    /* 单位处理：回填剥尾、提交补尾（已有则不动） */
    stripUnit(val, unit) {
      const v = (val || '').trim()
      return v.endsWith(unit) ? v.slice(0, -unit.length) : v
    },
    withUnit(val, unit) {
      const v = (val || '').trim()
      if (!v) return ''
      return v.endsWith(unit) ? v : v + unit
    }
  }
}
</script>

<style scoped>
.sort-input {
  width: 90px;
}
.sort-input >>> .el-input__inner {
  padding-left: 6px;
  padding-right: 34px;
  text-align: center;
}
.cover-thumb {
  width: 80px;
  height: 48px;
  border-radius: 4px;
  overflow: hidden;
  background: #f5f6f8;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}
.cover-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.cover-thumb--empty {
  color: #c0c4cc;
  font-size: 12px;
  border: 1px dashed #dcdfe6;
  background: #fafafa;
}
.cell-muted {
  color: #c0c4cc;
  font-size: 12px;
}

/* 操作列更多下拉 */
.more-dropdown {
  margin-left: 10px;
  vertical-align: middle;
}
.more-dropdown .el-button {
  color: #606266;
}

/* 配套设施管理弹框 */
.facility-manage-dialog >>> .el-dialog__body {
  padding: 14px 30px 4px;
}
.facility-intro-section {
  margin-bottom: 8px;
}
.facility-intro-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
}
.facility-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.facility-bar-title {
  margin: 0;
}
.facility-cards {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.facility-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 14px;
  border: 1px solid #ebeef5;
  border-radius: 10px;
  background: #fff;
  transition: box-shadow 0.2s;
}
.facility-row:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}
.facility-row.is-hidden {
  background: #fafafa;
}
.facility-row-imgs {
  flex: 0 0 auto;
  display: flex;
  gap: 6px;
}
.facility-img {
  width: 72px;
  height: 72px;
  border-radius: 6px;
  overflow: hidden;
  background: #f5f3f0;
}
.facility-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.facility-img--empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: #c0c4cc;
  font-size: 12px;
  border: 1px dashed #e0dcd6;
}
.facility-img--empty i {
  font-size: 20px;
}
.facility-row-main {
  flex: 1;
  min-width: 0;
}
.facility-row-head {
  display: flex;
  align-items: center;
  gap: 8px;
}
.facility-row-name {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}
.facility-cat-tag {
  color: #8e6a3f;
  background: #faf5ee;
  border-color: #efe4d3;
}
.facility-row-intro {
  margin: 5px 0 4px;
  font-size: 13px;
  color: #606266;
  line-height: 1.6;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.facility-row-sort {
  font-size: 12px;
  color: #a8abb2;
}
.facility-row-ops {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}
.facility-row-ops .el-button + .el-button {
  margin-left: 0;
}
.facility-form-dialog >>> .el-dialog__body {
  padding: 14px 30px 4px;
}

/* 分段标题（与其他模块一致） */
.form-section {
  margin-bottom: 18px;
  padding-bottom: 4px;
}
.form-section-title {
  font-size: 13px;
  font-weight: 500;
  color: var(--primary);
  border-left: 3px solid var(--primary);
  padding-left: 8px;
  margin: 16px 0 14px;
}
.form-tip {
  margin-left: 12px;
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
}

/* 房型管理弹框 */
.room-manage-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}
.room-manage-tip {
  font-size: 12px;
  color: #909399;
}
.room-cards {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.room-card {
  position: relative;
  display: flex;
  gap: 14px;
  padding: 14px;
  border: 1px solid #ebeef5;
  border-radius: 10px;
  background: #fff;
  transition: box-shadow 0.2s;
}
.room-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}
.room-card.is-hidden {
  background: #fafafa;
}
.room-card-cover {
  position: relative;
  flex: 0 0 168px;
  height: 112px;
  border-radius: 8px;
  overflow: hidden;
  background: #f5f3f0;
}
.room-card-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.cover-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: #c0c4cc;
  font-size: 12px;
}
.cover-placeholder i {
  font-size: 24px;
}
.cover-mask {
  position: absolute;
  left: 0;
  top: 0;
  padding: 2px 10px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 12px;
  border-radius: 0 0 8px 0;
}
.room-card-main {
  flex: 1;
  min-width: 0;
}
.room-card-head {
  display: flex;
  align-items: center;
  gap: 8px;
}
.room-card-name {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}
.room-card-desc {
  margin: 6px 0;
  font-size: 13px;
  color: #606266;
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.room-card-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  font-size: 12px;
  color: #8e6a3f;
}
.room-card-meta i {
  margin-right: 2px;
}
.room-card-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}
.facility-tag {
  font-size: 12px;
  color: #8e6a3f;
  background: #faf5ee;
  border: 1px solid #efe4d3;
  border-radius: 3px;
  padding: 1px 8px;
  line-height: 18px;
}
.room-card-ops {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}
.room-card-ops .el-button + .el-button {
  margin-left: 0;
}
.room-empty {
  padding: 36px 0 20px;
  text-align: center;
  color: #c0c4cc;
  font-size: 13px;
}
.room-empty i {
  font-size: 32px;
  display: block;
  margin-bottom: 8px;
}

/* 弹框内整体样式（与全局弹框适配） */
.stay-community-dialog >>> .el-dialog__body {
  padding: 14px 30px 4px;
}
.stay-community-dialog >>> .wechat-editor {
  min-height: 240px;
}
.room-form-dialog >>> .el-dialog__body {
  padding: 14px 30px 4px;
}
</style>
